<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import LeadScoringConfigAPI from 'dashboard/api/leadScoringConfig';
import Switch from 'dashboard/components-next/switch/Switch.vue';

// Config do Lead Scoring — feature nativa (gated por lead_scoring flag) extraída
// do namespace VOIP. A IA textual e seu modelo são geridos pela plataforma;
// o admin configura horário, prompt livre e limite por execução.
const cfg = ref({
  enabled: false,
  cron_expression: '0 2 * * *',
  max_per_run: 500,
  changed_only: true,
  custom_prompt: '',
  default_prompt: '',
  last_run_at: null,
  text_ai_ready: false,
  // Filtros de escopo (v79+): inbox_ids vazio = todas as inboxes.
  inbox_ids: [],
  ignore_whatsapp_groups: true,
});
const availableInboxes = ref([]);
const availableLabels = ref([]);
const availableFunnels = ref([]);
const previewSystemPrompt = ref('');
const showPreview = ref(false);
const promptTextareaRef = ref(null);

const saving = ref(false);
const flash = ref(false);
const running = ref(false);
const runFlash = ref('');
const runFlashSuccess = ref(false);

// Hora em 2 selects (HH / MM) em vez de <input type="time"> — o type=time
// respeita o locale do SO do usuario, entao em maquinas com en-US cai em
// AM/PM mesmo com lang="pt-BR". Selects numericos sao imunes a isso.
const simpleHour = ref('02');
const simpleMinute = ref('00');
const advancedMode = ref(false);

const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));
// Step de 5 minutos (00, 05, 10, ..., 55) pra manter UI enxuta.
const MINUTE_OPTIONS = Array.from({ length: 12 }, (_, i) => String(i * 5).padStart(2, '0'));

const parsedFromCron = computed(() => {
  const parts = (cfg.value.cron_expression || '').trim().split(/\s+/);
  const isSimple =
    parts.length === 5 &&
    /^\d{1,2}$/.test(parts[0]) &&
    /^\d{1,2}$/.test(parts[1]) &&
    parts[2] === '*' &&
    parts[3] === '*' &&
    parts[4] === '*';
  if (!isSimple) return null;
  return {
    hour: String(parts[1]).padStart(2, '0'),
    minute: String(parts[0]).padStart(2, '0'),
  };
});

function syncSimpleFromCron() {
  const parsed = parsedFromCron.value;
  if (parsed) {
    simpleHour.value = parsed.hour;
    simpleMinute.value = parsed.minute;
    advancedMode.value = false;
  } else {
    advancedMode.value = true;
  }
}

function syncCronFromSimple() {
  cfg.value.cron_expression = `${parseInt(simpleMinute.value, 10)} ${parseInt(simpleHour.value, 10)} * * *`;
}

watch([simpleHour, simpleMinute], () => {
  if (!advancedMode.value) syncCronFromSimple();
});

// Aplica payload vindo do backend (show ou update) no state local,
// separando available_inboxes (UI only) e normalizando inbox_ids.
// Chamado por loadConfig E save — evita drift de normalizacao.
function applyServerPayload(data) {
  if (!data) {
    // eslint-disable-next-line no-console
    console.warn('[LeadScoringSettings] applyServerPayload: data vazio/null');
    return;
  }
  availableInboxes.value = Array.isArray(data.available_inboxes) ? data.available_inboxes : availableInboxes.value;
  availableLabels.value  = Array.isArray(data.available_labels)  ? data.available_labels  : availableLabels.value;
  availableFunnels.value = Array.isArray(data.available_funnels) ? data.available_funnels : availableFunnels.value;
  previewSystemPrompt.value = data.preview_system_prompt || previewSystemPrompt.value;

  const {
    available_inboxes: _ai,
    available_labels: _al,
    available_funnels: _af,
    preview_system_prompt: _pp,
    ...configFields
  } = data;

  // IMPORTANTE: Object.assign preserva a MESMA referencia do objeto cfg
  // (Vue reativo). Substituir via `cfg.value = { ... }` quebrava a
  // ligacao reativa do Switch e outros componentes em alguns browsers,
  // fazendo parte da UI sumir apos salvar/carregar.
  Object.assign(cfg.value, configFields);

  if (!cfg.value.custom_prompt) {
    cfg.value.custom_prompt = cfg.value.default_prompt || '';
  }
  cfg.value.inbox_ids = Array.isArray(cfg.value.inbox_ids)
    ? cfg.value.inbox_ids.map(Number).filter(Number.isFinite)
    : [];
  // Garante booleanos (backend pode retornar como true/false strings em
  // alguns paths esquisitos).
  cfg.value.enabled = Boolean(cfg.value.enabled);
  cfg.value.changed_only = Boolean(cfg.value.changed_only);
  cfg.value.ignore_whatsapp_groups = cfg.value.ignore_whatsapp_groups !== false;

  syncSimpleFromCron();
}

// Insere texto no cursor do textarea do prompt. Usado pelos chips de
// etiqueta e estagio — click insere o nome exato no ponto onde o admin
// estava digitando.
function insertIntoPrompt(snippet) {
  const ta = promptTextareaRef.value;
  const current = cfg.value.custom_prompt || '';
  if (!ta) {
    cfg.value.custom_prompt = current + snippet;
    return;
  }
  const start = ta.selectionStart ?? current.length;
  const end   = ta.selectionEnd ?? current.length;
  cfg.value.custom_prompt = current.slice(0, start) + snippet + current.slice(end);
  // Reposiciona cursor apos o texto inserido no proximo tick.
  setTimeout(() => {
    ta.focus();
    const newPos = start + snippet.length;
    ta.setSelectionRange(newPos, newPos);
  }, 0);
}

const loadError = ref('');

async function loadConfig() {
  loadError.value = '';
  try {
    const { data } = await LeadScoringConfigAPI.show();
    applyServerPayload(data);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('[LeadScoringSettings] load falhou', e);
    loadError.value = e?.response?.data?.error || e?.message || 'Falha ao carregar configuração';
    throw e;
  }
}

function toggleInbox(inboxId) {
  const id = Number(inboxId);
  const idx = cfg.value.inbox_ids.indexOf(id);
  if (idx >= 0) {
    cfg.value.inbox_ids.splice(idx, 1);
  } else {
    cfg.value.inbox_ids.push(id);
  }
}

const allInboxesSelected = computed(() => cfg.value.inbox_ids.length === 0);
const inboxSummary = computed(() => {
  if (allInboxesSelected.value) return 'Todas as caixas de entrada';
  const n = cfg.value.inbox_ids.length;
  return `${n} caixa${n === 1 ? '' : 's'} selecionada${n === 1 ? '' : 's'}`;
});

onMounted(loadConfig);

const saveError = ref('');

async function save() {
  saving.value = true;
  saveError.value = '';
  try {
    if (!advancedMode.value) syncCronFromSimple();
    const payload = {
      enabled: cfg.value.enabled,
      cron_expression: cfg.value.cron_expression,
      max_per_run: cfg.value.max_per_run,
      changed_only: cfg.value.changed_only,
      custom_prompt: cfg.value.custom_prompt,
      inbox_ids: cfg.value.inbox_ids || [],
      ignore_whatsapp_groups: cfg.value.ignore_whatsapp_groups,
    };
    const { data } = await LeadScoringConfigAPI.update(payload);
    applyServerPayload(data);
    flash.value = true;
    setTimeout(() => {
      flash.value = false;
    }, 2000);
  } catch (e) {
    // Nao deixa erro silencioso quebrar a UI. Anteriormente o save sem
    // catch em caso de erro deixava o cfg em estado parcial e alguns
    // campos sumiam sem feedback.
    // eslint-disable-next-line no-console
    console.error('[LeadScoringSettings] save falhou', e);
    const msg = e?.response?.data?.errors || e?.response?.data?.error || e?.message;
    saveError.value = `Erro ao salvar: ${Array.isArray(msg) ? msg.join('; ') : (msg || 'tente novamente')}`;
    // Recarrega config do servidor pra garantir estado consistente.
    try { await loadConfig(); } catch (_) { /* noop — ja temos erro */ }
  } finally {
    saving.value = false;
  }
}

const nextRunDescription = computed(() => {
  if (!cfg.value.enabled) return 'Desativado';
  if (advancedMode.value) return `Conforme expressão cron "${cfg.value.cron_expression}"`;
  return `Todos os dias às ${simpleHour.value}:${simpleMinute.value}`;
});

function resetToDefaultPrompt() {
  cfg.value.custom_prompt = cfg.value.default_prompt || '';
}

// Trigger manual do scoring — enfileira ScoreLeadJob pros contatos
// candidatos AGORA (respeita max_per_run e changed_only mas ignora
// config.enabled?). Processamento acontece em background no Sidekiq,
// entao o feedback aqui e so "enfileirado com sucesso".
async function runNow() {
  if (running.value) return;
  if (!cfg.value.text_ai_ready) {
    runFlash.value = 'A IA interna está indisponível. Fale com o suporte.';
    setTimeout(() => { runFlash.value = ''; }, 3500);
    return;
  }
  running.value = true;
  runFlash.value = '';
  try {
    const { data } = await LeadScoringConfigAPI.runNow();
    const n = data?.enqueued ?? 0;
    runFlash.value = n === 0
      ? 'Nenhum lead elegivel (sem atividade nova ou sem contatos).'
      : `${n} lead${n === 1 ? '' : 's'} enfileirado${n === 1 ? '' : 's'} — processamento em background.`;
    runFlashSuccess.value = n > 0;
    await loadConfig();
  } catch (e) {
    runFlash.value = `Falha ao disparar: ${e?.response?.data?.error || e.message}`;
  } finally {
    running.value = false;
    setTimeout(() => { runFlash.value = ''; }, 6000);
  }
}
</script>

<template>
  <div class="w-full h-full overflow-y-auto bg-n-surface-1">
    <div class="max-w-3xl mx-auto p-6 space-y-6 pb-16">
      <header>
        <h1 class="text-2xl font-extrabold text-n-slate-12">Lead Scoring</h1>
        <p class="text-sm text-n-slate-11 mt-1">
          A IA analisa automaticamente o histórico de conversas de cada contato
          (texto, transcrições de áudio e notas internas) e atribui score 0-100
          + perfil HOT/WARM/COLD. O score aparece no painel do contato durante
          qualquer atendimento.
        </p>
      </header>

      <div
        v-if="loadError"
        class="border border-n-ruby-6 bg-n-ruby-3 text-n-ruby-11 rounded-2xl p-4 text-sm flex items-start gap-2"
      >
        <span class="i-ri-error-warning-line size-4 shrink-0 mt-0.5" />
        <div class="flex-1">
          <p class="font-medium">Falha ao carregar configuração</p>
          <p class="text-xs mt-1">{{ loadError }}</p>
          <button
            type="button"
            class="text-xs underline mt-1 hover:opacity-80"
            @click="loadConfig"
          >Tentar de novo</button>
        </div>
      </div>

      <!-- Toggle master + platform AI readiness -->
      <section class="border border-n-weak rounded-2xl p-5 bg-n-solid-1 space-y-4">
        <header class="flex items-start justify-between gap-4">
          <div class="flex-1">
            <h2 class="font-semibold text-n-slate-12">Ativar scoring automático</h2>
            <p class="text-xs text-n-slate-11 mt-1 leading-relaxed">
              Todo dia na hora configurada, o sistema processa contatos com
              atividade recente e atualiza o score. Custo ~R$ 0,01 por lead.
            </p>
          </div>
          <div class="inline-flex items-center gap-2 text-sm shrink-0">
            <Switch v-model="cfg.enabled" />
            <span :class="cfg.enabled ? 'text-n-slate-12 font-medium' : 'text-n-slate-11'">
              {{ cfg.enabled ? 'Ativado' : 'Desativado' }}
            </span>
          </div>
        </header>

        <div
          class="rounded-xl border px-3 py-2 text-xs"
          :class="cfg.text_ai_ready
            ? 'border-n-teal-7 bg-n-teal-3 text-n-teal-11'
            : 'border-n-amber-7 bg-n-amber-3 text-n-amber-11'"
        >
          <p class="font-medium">
            {{ cfg.text_ai_ready ? 'IA interna disponível' : 'IA interna indisponível' }}
          </p>
          <p class="mt-0.5 opacity-90">
            {{ cfg.text_ai_ready
              ? 'Tudo pronto. O uso de IA já está incluído na plataforma.'
              : 'Não é necessário informar uma chave. Fale com o suporte para reativar o serviço.' }}
          </p>
        </div>
      </section>

      <!-- Agendamento -->
      <section class="border border-n-weak rounded-2xl p-5 bg-n-solid-1 space-y-4">
        <h2 class="font-semibold text-n-slate-12">Agendamento</h2>

        <div v-if="!advancedMode" class="space-y-2">
          <label class="block text-sm font-medium text-n-slate-12">Rodar todos os dias às</label>
          <div class="flex items-center gap-2">
            <select
              v-model="simpleHour"
              class="rounded-xl border border-n-weak bg-n-alpha-1 px-3 py-2 text-sm text-n-slate-12 w-20"
            >
              <option v-for="h in HOUR_OPTIONS" :key="h" :value="h">{{ h }}</option>
            </select>
            <span class="text-n-slate-12 font-medium">:</span>
            <select
              v-model="simpleMinute"
              class="rounded-xl border border-n-weak bg-n-alpha-1 px-3 py-2 text-sm text-n-slate-12 w-20"
            >
              <option v-for="m in MINUTE_OPTIONS" :key="m" :value="m">{{ m }}</option>
            </select>
            <span class="text-xs text-n-slate-11">(24h, horário do servidor)</span>
            <button
              type="button"
              class="ml-auto text-xs text-n-slate-11 hover:text-n-slate-12 underline"
              @click="advancedMode = true"
            >Modo avançado</button>
          </div>
          <p class="text-[11px] text-n-slate-11">
            Dica: rode de madrugada (ex: 02:00) pra não impactar uso do sistema.
          </p>
        </div>

        <div v-else class="space-y-2">
          <div class="flex items-center justify-between">
            <label class="block text-sm font-medium text-n-slate-12">Expressão Cron</label>
            <button
              type="button"
              class="text-xs text-n-slate-11 hover:text-n-slate-12 underline"
              @click="advancedMode = false; syncSimpleFromCron()"
            >Voltar pro modo simples</button>
          </div>
          <input
            v-model="cfg.cron_expression"
            placeholder="0 2 * * *"
            class="w-full font-mono rounded-xl border border-n-weak bg-n-alpha-1 px-3 py-2 text-sm text-n-slate-12"
          />
          <p class="text-[11px] text-n-slate-11">
            Formato: min hora dia-mes mes dia-semana · Valide em <a
              href="https://crontab.guru"
              target="_blank"
              rel="noopener"
              class="underline"
            >crontab.guru</a>.
          </p>
        </div>

        <div class="grid md:grid-cols-2 gap-3">
          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-1">Limite por execução</label>
            <input
              v-model.number="cfg.max_per_run"
              type="number"
              min="1"
              max="5000"
              step="50"
              class="w-full rounded-xl border border-n-weak bg-n-alpha-1 px-3 py-2 text-sm text-n-slate-12"
            />
            <p class="text-[11px] text-n-slate-11 mt-1">
              Quantos leads processar por noite.
            </p>
          </div>
          <div class="flex items-end pb-4">
            <label class="inline-flex items-start gap-2 text-sm text-n-slate-12">
              <input
                v-model="cfg.changed_only"
                type="checkbox"
                class="rounded border-n-weak mt-0.5"
              />
              <span>
                Só analisar contatos com atividade nova
                <span class="block text-[11px] text-n-slate-11 mt-0.5 font-normal">
                  Pula contatos sem mensagem desde o último run
                </span>
              </span>
            </label>
          </div>
        </div>

        <div class="flex items-center gap-4 text-xs text-n-slate-11 bg-n-alpha-1 rounded-xl px-3 py-2">
          <span class="inline-flex items-center gap-1">
            <span class="i-ri-calendar-schedule-line size-3.5" />
            <strong class="text-n-slate-12">{{ nextRunDescription }}</strong>
          </span>
          <span v-if="cfg.last_run_at">
            · Último run: {{ new Date(cfg.last_run_at).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false }) }}
          </span>
        </div>
      </section>

      <!-- Escopo de análise -->
      <section class="border border-n-weak rounded-2xl p-5 bg-n-solid-1 space-y-4">
        <h2 class="font-semibold text-n-slate-12">Escopo da análise</h2>

        <!-- Caixas de entrada -->
        <div>
          <label class="block text-sm font-medium text-n-slate-12 mb-1">Caixas de entrada</label>
          <p class="text-[11px] text-n-slate-11 mb-2">
            Marque as inboxes cujas conversas devem contar no scoring.
            <strong>{{ inboxSummary }}</strong> (nenhuma marcada = todas).
          </p>
          <div
            v-if="availableInboxes.length"
            class="grid sm:grid-cols-2 gap-2 border border-n-weak rounded-xl p-3 bg-n-alpha-1"
          >
            <label
              v-for="inbox in availableInboxes"
              :key="inbox.id"
              class="flex items-start gap-2 text-sm text-n-slate-12 cursor-pointer"
            >
              <input
                type="checkbox"
                :checked="cfg.inbox_ids.includes(Number(inbox.id))"
                class="mt-0.5 rounded border-n-weak"
                @change="toggleInbox(inbox.id)"
              />
              <span class="flex-1">
                <span class="font-medium">{{ inbox.name }}</span>
                <span class="block text-[10px] text-n-slate-11 uppercase">{{ inbox.channel_type }}</span>
              </span>
            </label>
          </div>
          <p v-else class="text-xs text-n-slate-11 italic">Nenhuma inbox cadastrada na conta.</p>
        </div>

        <!-- Ignorar grupos -->
        <label class="flex items-start gap-2 text-sm text-n-slate-12 cursor-pointer">
          <input
            v-model="cfg.ignore_whatsapp_groups"
            type="checkbox"
            class="mt-0.5 rounded border-n-weak"
          />
          <span>
            Ignorar conversas de grupo do WhatsApp
            <span class="block text-[11px] text-n-slate-11 mt-0.5 font-normal">
              Grupos têm centenas de mensagens irrelevantes pro scoring individual.
              Recomendado manter ativo — economiza tokens.
            </span>
          </span>
        </label>

        <!-- Groups engagement context (always injected when membership exists) -->
        <div class="rounded-xl border border-n-weak bg-n-alpha-1 px-3 py-3 space-y-1">
          <div class="flex items-center gap-2 text-sm font-medium text-n-slate-12">
            <span class="i-ri-group-line size-4 text-n-slate-11" />
            Engajamento em WhatsApp Groups
          </div>
          <p class="text-[11px] text-n-slate-11 leading-relaxed">
            Mesmo com transcript de sala ignorado, o scoring continua recebendo
            <code class="font-mono">groups_context</code>: nome do grupo, level,
            score de engajamento, launch_id e última interação — sinal operacional
            por membership ativa do contato.
          </p>
          <p class="text-[11px] text-n-slate-11">
            Notificações HOT usam o threshold/cooldown do serviço de scoring
            (cruzamento ≥80 / saltos grandes); Groups alimenta o contexto, não
            substitui a régua de score.
          </p>
        </div>
      </section>

      <!-- Prompt customizado -->
      <section class="border border-n-weak rounded-2xl p-5 bg-n-solid-1 space-y-3">
        <header class="flex items-center justify-between gap-3 flex-wrap">
          <h2 class="font-semibold text-n-slate-12">O que a IA deve considerar?</h2>
          <div class="flex items-center gap-3">
            <button
              type="button"
              class="text-xs text-n-slate-11 hover:text-n-slate-12 underline"
              @click="showPreview = true"
            >Ver prompt final</button>
            <button
              type="button"
              class="text-xs text-n-slate-11 hover:text-n-slate-12 underline"
              @click="resetToDefaultPrompt"
            >Usar prompt padrão</button>
          </div>
        </header>
        <p class="text-xs text-n-slate-11">
          Descreva como funciona a pontuação do seu negócio — quais critérios
          pesam mais, o que é um lead quente pra você. Pode referenciar
          etiquetas e estágios do Kanban (clique nos chips abaixo pra inserir).
          Formato de saída (score, perfil, critérios, sugestões) é retornado
          sempre — não precisa pedir.
        </p>
        <textarea
          ref="promptTextareaRef"
          v-model="cfg.custom_prompt"
          rows="10"
          class="w-full font-mono text-xs rounded-xl border border-n-weak bg-n-alpha-1 px-3 py-2 text-n-slate-12"
          placeholder='Ex: avalie engajamento nas respostas, sinais de interesse em comprar... Etiqueta "urgente" soma +15. Estágio "Agendado" garante score mínimo 60.'
        />

        <!-- Chips de etiquetas e estágios disponíveis -->
        <div v-if="availableLabels.length" class="space-y-1">
          <p class="text-[11px] text-n-slate-11 font-medium uppercase">Etiquetas (clique pra inserir)</p>
          <div class="flex flex-wrap gap-1.5">
            <button
              v-for="label in availableLabels"
              :key="'lbl-' + label"
              type="button"
              class="px-2 py-0.5 rounded-md bg-n-alpha-2 border border-n-weak text-xs text-n-slate-12 hover:border-n-brand hover:bg-n-brand/10 transition-colors"
              @click="insertIntoPrompt(`&quot;${label}&quot;`)"
            >
              {{ label }}
            </button>
          </div>
        </div>

        <div v-if="availableFunnels.length" class="space-y-1">
          <p class="text-[11px] text-n-slate-11 font-medium uppercase">Funis e estágios (clique pra inserir)</p>
          <p class="text-[11px] text-n-slate-11">
            Chips de estágio incluem o nome do funil pra ficar inequívoco no prompt.
          </p>
          <div class="space-y-1">
            <div v-for="funnel in availableFunnels" :key="'fn-' + funnel.id" class="flex flex-wrap gap-1.5 items-center">
              <span class="text-[11px] text-n-slate-11 mr-1">{{ funnel.name }}:</span>
              <button
                v-for="stage in funnel.stages"
                :key="'st-' + funnel.id + '-' + stage"
                type="button"
                class="px-2 py-0.5 rounded-md bg-n-alpha-2 border border-n-weak text-xs text-n-slate-12 hover:border-n-brand hover:bg-n-brand/10 transition-colors"
                :title="`Insere: &quot;${funnel.name} → ${stage}&quot;`"
                @click="insertIntoPrompt(`&quot;${funnel.name} → ${stage}&quot;`)"
              >
                {{ stage }}
              </button>
            </div>
          </div>
        </div>

        <p v-if="!availableLabels.length && !availableFunnels.length" class="text-[11px] text-n-slate-11 italic">
          Nenhuma etiqueta ou funil cadastrado ainda. Crie em Configurações → Etiquetas e Kanban → Funis pra poder referenciá-los aqui.
        </p>
      </section>

      <!-- Modal: preview do prompt final -->
      <div
        v-if="showPreview"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
        @click.self="showPreview = false"
      >
        <div class="bg-n-solid-1 border border-n-weak rounded-2xl shadow-xl max-w-3xl w-full max-h-[80vh] flex flex-col">
          <header class="flex items-center justify-between px-5 py-3 border-b border-n-weak">
            <h3 class="font-semibold text-n-slate-12">Instruções enviadas à IA</h3>
            <button
              type="button"
              class="text-n-slate-11 hover:text-n-slate-12"
              @click="showPreview = false"
            >✕</button>
          </header>
          <div class="p-5 overflow-y-auto flex-1">
            <p class="text-xs text-n-slate-11 mb-3">
              Isso é exatamente o que a IA interna recebe como instrução principal
              em cada análise. Inclui o que você escreveu + lembrete automático
              sobre regras por etiqueta/estágio + instruções de formato JSON.
            </p>
            <pre class="font-mono text-xs bg-n-alpha-1 rounded-xl p-4 whitespace-pre-wrap text-n-slate-12 border border-n-weak">{{ previewSystemPrompt }}</pre>
          </div>
          <footer class="px-5 py-3 border-t border-n-weak flex justify-end">
            <button
              type="button"
              class="px-4 py-2 rounded-xl bg-n-brand text-white text-sm font-medium"
              @click="showPreview = false"
            >Fechar</button>
          </footer>
        </div>
      </div>

      <!-- Save + Run now -->
      <div class="flex items-center gap-3 flex-wrap">
        <button
          type="button"
          class="px-4 py-2 rounded-xl bg-n-brand text-white text-sm font-medium hover:bg-n-brand/90 disabled:opacity-50"
          :disabled="saving"
          @click="save"
        >
          {{ saving ? 'Salvando...' : 'Salvar configuração' }}
        </button>
        <button
          type="button"
          class="px-4 py-2 rounded-xl border border-n-brand text-n-brand text-sm font-medium hover:bg-n-brand/10 disabled:opacity-50 inline-flex items-center gap-2"
          :disabled="running || saving || !cfg.text_ai_ready"
          :title="cfg.text_ai_ready ? 'Enfileira a análise dos contatos agora' : 'IA interna indisponível'"
          @click="runNow"
        >
          <span :class="['i-ri-play-line', 'size-4']" />
          {{ running ? 'Disparando...' : 'Analisar agora' }}
        </button>
        <p v-if="flash" class="text-sm text-n-teal-11 inline-flex items-center gap-1">
          <span class="i-ri-check-line size-4" /> Salvo
        </p>
        <p v-if="saveError" class="text-sm text-n-ruby-11 inline-flex items-center gap-1">
          <span class="i-ri-error-warning-line size-4 shrink-0" />
          {{ saveError }}
        </p>
        <p
          v-if="runFlash"
          class="text-sm inline-flex items-center gap-1"
          :class="runFlashSuccess ? 'text-n-teal-11' : 'text-n-amber-11'"
        >
          <span :class="[runFlashSuccess ? 'i-ri-checkbox-circle-line' : 'i-ri-information-line', 'size-4']" />
          {{ runFlash }}
        </p>
      </div>
    </div>
  </div>
</template>
